<template>
  <default-textual-section v-if="$store.state.displayType === 'textual'" :menu="menu" :section="section"
                           :class="containerClasses" :menu-item-component="menuItemComponent"
                           class="menu--section-chi" item-group-layout="title-above"
                           is-title-box-visible>
    <template #sectionTitleImage>
      <slot name="sectionTitleImage"></slot>
    </template>
    <template #beforeBottomContentArea>
      <slot name="beforeBottomContentArea"></slot>
    </template>
  </default-textual-section>
  <default-visual-section v-else :menu="menu" :section="section"
                          :class="containerClasses" :menu-item-component="menuItemComponent"
                          class="menu--section-chi" item-group-layout="title-above"
                          is-title-box-visible>
    <template #sectionTitleImage>
      <slot name="sectionTitleImage"></slot>
    </template>
    <template #beforeBottomContentArea>
      <slot name="beforeBottomContentArea"></slot>
    </template>
  </default-visual-section>
</template>

<script lang="ts">
/**
 * Custom component for Bimini default menu sections.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 2.2.0
 * @copyright  Copyright (C) 2023-2026, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 * @changelog
 *
 *  2023-06-07 (1.0.0) (jhoward)
 *    - Copy from PanchoWillies_defaultSection.vue component to Chi_defaultSection.vue component.
 *
 *  2023-08-18 (1.0.1) (jhoward)
 *    - Add custom intro page CSS to <Chi_defaultSection>.
 *
 *  2023-08-28 (1.1.0) (jhoward)
 *    - Use <menu-version> in <Chi_defaultSection>.
 *
 *  2025-02-11 (1.2.0) (jhoward)
 *    - Add <menu-schedules> to the <Chi_defaultSection> component if in admin view.
 *
 *  2025-04-08 (2.0.0) (jhoward)
 *    - Use <default-textual-section> and <default-visual-section> in <Chi_defaultSection>.
 *    - Add textual and visual menu CSS tweaks to <Chi_defaultSection>.
 *
 * 2026-02-17 (2.1.0) (jhoward)
 *   - Add menuItemComponent prop to App\Library\Js\Components\Menu\Section\Chi_defaultSection.vue to allow dynamic item component selection.
 *   - Add slots for sectionTitleImage and beforeBottomContentArea to support custom section content.
 *   - Update template to use new props and slots, increasing flexibility for section rendering.
 *
 * 2026-07-20 (2.2.0) (jhoward)
 *   - Add menuItemComponent prop and slots for sectionTitleImage and beforeBottomContentArea to App\Library\Js\Components\Menu\Section\Chi_defaultSection.vue.
 */

import {defineComponent, PropType} from "vue";
import {ALLOWED_ITEM_COMPONENTS, Menu, MenuSection} from "../defs";
import DefaultVisualSection from "./visual/defaultVisualSection.vue";
import DefaultTextualSection from "./textual/defaultTextualSection.vue";

export default defineComponent({
  components: {
    DefaultTextualSection,
    DefaultVisualSection,
  },
  name: "Chi_defaultSection",
  props: {
    section: {
      type: Object as PropType<MenuSection>,
      required: true
    },
    menu: {
      type: Object as PropType<Menu>,
      required: true
    },
    sectionModifier: {
      type: String,
      default: ''
    },
    menuItemComponent: {
      type: String,
      default: 'chi-default-item',
      validate: (value: string) => '' === value || ALLOWED_ITEM_COMPONENTS.indexOf(value)
    },
  },
  computed: {
    containerClasses: function () {
      return {
        'menu--section-desserts': this.section.ms_title.toLowerCase() === 'desserts',
      };
    }
  },
})
</script>

<style>
html .visual_menu--container-chi,
html .textual_menu--container-chi {
  background-color: #FFFFFF;
}

.visual_menu--container-chi .visual_menu--page.visual_menu--content_top h3,
.textual_menu--container-chi .menu--page_content-top h3 {
  font-family: "calibri", sans-serif;
  font-weight: 300;
  font-style: normal;
  color: inherit;
  font-variant: small-caps;
  text-align: center;
  margin-bottom: 0.6em;
}

.visual_menu--container-chi .visual_menu--page.visual_menu--content_top p,
.textual_menu--container-chi .menu--page_content-top p {
  text-align: justify;
  font-size: 0.8em;
  font-weight: 300;
}
</style>

<style scoped>
._menu--section {
  color: #231f20;
  font-size: 0.8em;
}

.menu--section:deep(.menu--section-desserts .chi_default_item--container) {
  grid-template-columns: 1fr 0.8fr;
}

.menu--container-chi:deep(.menu--item_subgroup) {
  grid-row-gap: 0.2em;
  grid-column-gap: 2em;
}

.menu--section-chi:deep(.menu--section_title),
.menu--section-chi:deep(.menu--section_subtitle) {
  font-family: "calibri", sans-serif;
  font-weight: 300;
  font-style: normal;
  color: inherit;
  font-variant: small-caps;
  width: 100%;
  padding: 0 1em 0.1em 1em;
  box-sizing: border-box;
  overflow: initial;
  text-align: center;
  max-width: initial;
}


.menu--section-chi:deep(.menu--section_subtitle) {
  font-size: 1.2em;
  margin-bottom: 1em;
}

.menu--section-chi:deep(.menu--section_title::before) {
  position: absolute;
  content: "";
  top: 0.6em;
  height: 2em;
  left: -0.82em;
  width: 0.05em;
  background-color: #a61d21;
}

.menu--section-chi:deep(.menu--section_title::after) {
  position: absolute;
  content: "";
  bottom: 0.05em;
  left: -1.1em;
  right: 3em;
  height: 0.05em;
  background-color: #bc5558;
}

.menu--section-chi:deep(.menu--section_title_box) {
  position: absolute;
  top: 0.95em;
  left: -1em;
  border-color: #ddae5b;
  border-width: 0 0.05em 0.05em 0;
  border-style: solid;
  width: 0.4em;
  height: 0.75em;
}

@media (max-width: 1100px) {
  .textual_menu--section.menu--section-chi:deep(.menu--section_title::before) {
    left: 0.68em;
  }

  .textual_menu--section.menu--section-chi:deep(.menu--section_title::after) {
    left: 0.4em;
  }

  .textual_menu--section.menu--section-chi:deep(.menu--section_title_box) {
    left: 0.5em;
  }
}

.menu--section-chi:deep(.menu--section_title_box-right_angle) {
  width: 0;
  height: 0;
  left: 0.9em;
}

.menu--section-chi:deep(.menu--section_title_box::before) {
  position: absolute;
  content: '';
  top: 0;
  left: 100%;
  width: 3em;
  height: 0.05em;
  background-color: #ddae5b;
}

.menu--section-chi:deep(.menu--section_title_box::after) {
  position: absolute;
  content: '';
  top: 100%;
  left: 0;
  height: 4em;
  width: 0.05em;
  background-color: #ddae5b;
}

.menu--section-chi:deep(.menu--section_subgroup_title) {
  color: #231f20;
}

/*START PRICING RULES OVERRIDES*/
.menu--section-chi:deep(.menu--item_group:first-of-type) {
  margin-top: 1.3em;
}

.menu--section-chi:deep(.menu--item_subgroup .menu--section_subgroup_title) {
  font-weight: lighter;
}

.menu--section-chi:deep(.menu--item_subgroup .menu--section_subgroup_title span:first-child) {
  font-weight: lighter;
  text-transform: uppercase;
}

.menu--section-chi:deep(.menu--item_subgroup:first-of-type .menu--section_subgroup_title) {
  margin-top: 0;
}

/*.menu--section-chi.menu--section-show_pricing_header:deep(.menu--item),
.menu--section-chi.menu--section-show_pricing_header:deep(.menu--section_subgroup_title) {
  grid-gap: 0;
}

.menu--section-chi.menu--section-show_pricing_header:deep(.menu--item_subgroup-show_BIN .menu--item),
.menu--section-chi.menu--section-show_pricing_header:deep(.menu--item_subgroup-show_BIN .menu--section_subgroup_title) {
  grid-template-columns: 0.6fr 3fr;
}

.menu--section-chi.menu--section-show_pricing_header:deep(.menu--item_subgroup[data-pricing-type-count="1"] .menu--item),
.menu--section-chi.menu--section-show_pricing_header:deep(.menu--item_subgroup[data-pricing-type-count="1"] .menu--section_subgroup_title) {
  grid-template-columns: 3.6fr 0.3fr;
}

.menu--section-chi.menu--section-show_pricing_header:deep(.menu--item_subgroup-show_BIN[data-pricing-type-count="1"] .menu--item),
.menu--section-chi.menu--section-show_pricing_header:deep(.menu--item_subgroup-show_BIN[data-pricing-type-count="1"] .menu--section_subgroup_title) {
  grid-template-columns: 0.6fr 3fr 0.4fr;
}

.menu--section-chi.menu--section-show_pricing_header:deep(.menu--item_subgroup[data-pricing-type-count="2"] .menu--item),
.menu--section-chi.menu--section-show_pricing_header:deep(.menu--item_subgroup[data-pricing-type-count="2"] .menu--section_subgroup_title) {
  grid-template-columns: 3.6fr 0.4fr 0.4fr;
}

.menu--section-chi.menu--section-show_pricing_header:deep(.menu--item_subgroup-show_BIN[data-pricing-type-count="2"] .menu--item),
.menu--section-chi.menu--section-show_pricing_header:deep(.menu--item_subgroup-show_BIN[data-pricing-type-count="2"] .menu--section_subgroup_title) {
  grid-template-columns: 0.6fr 3fr 0.4fr 0.4fr;
}

.menu--section-chi.menu--section-show_pricing_header:deep(.menu--item_subgroup[data-pricing-type-count="3"] .menu--item),
.menu--section-chi.menu--section-show_pricing_header:deep(.menu--item_subgroup[data-pricing-type-count="3"] .menu--section_subgroup_title) {
  grid-template-columns: 3.6fr 0.6fr 0.5fr 0.5fr;
}

.menu--section-chi.menu--section-show_pricing_header:deep(.menu--item_subgroup-show_BIN[data-pricing-type-count="3"] .menu--item),
.menu--section-chi.menu--section-show_pricing_header:deep(.menu--item_subgroup-show_BIN[data-pricing-type-count="3"] .menu--section_subgroup_title) {
  grid-template-columns: 0.6fr 3fr 0.5fr 0.5fr 0.5fr;
}

.menu--section-chi.menu--section-show_pricing_header:deep(.menu--item_subgroup[data-pricing-type-count="4"] .menu--item),
.menu--section-chi.menu--section-show_pricing_header:deep(.menu--item_subgroup[data-pricing-type-count="4"] .menu--section_subgroup_title) {
  grid-template-columns: 3.6fr 0.5fr 0.5fr 0.5fr 0.5fr;
}

.menu--section-chi.menu--section-show_pricing_header:deep(.menu--item_subgroup-show_BIN[data-pricing-type-count="4"] .menu--item),
.menu--section-chi.menu--section-show_pricing_header:deep(.menu--item_subgroup-show_BIN[data-pricing-type-count="4"] .menu--section_subgroup_title) {
  grid-template-columns: 0.6fr 3fr 0.5fr 0.5fr 0.5fr 0.5fr;
}

.menu--section-chi.menu--section-show_pricing_header:deep(.menu--item_subgroup[data-pricing-type-count="5"] .menu--item),
.menu--section-chi.menu--section-show_pricing_header:deep(.menu--item_subgroup[data-pricing-type-count="5"] .menu--section_subgroup_title) {
  grid-template-columns: 3.6fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr;
}

.menu--section-chi.menu--section-show_pricing_header:deep(.menu--item_subgroup-show_BIN[data-pricing-type-count="5"] .menu--item),
.menu--section-chi.menu--section-show_pricing_header:deep(.menu--item_subgroup-show_BIN[data-pricing-type-count="5"] .menu--section_subgroup_title) {
  grid-template-columns: 0.6fr 3fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr;
}*/

/*END PRICING RULES OVERRIDES*/
</style>